import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
	getTiersWithoutPrices,
	getSeshProStripeProductId,
	isSeshProProduct,
	PaymentInterval,
	PaymentIntervalToActiveName,
	StripeProductId,
} from "../../../../../../common/premium_tiers";
import { common_utils } from "../../../../../../common/common_utils";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { SeshAddonInput } from "../base_components/sesh_addon_input";
import { PricingTier } from "./pricing_tier";
import { WithTooltip } from "../tooltips/with_tooltip";
import { trpc } from "../../../utils/trpc";
import { GuildListContext } from "../../../contexts";
import { UpgradeChoiceModal, UpgradeableLicenseInfo } from "./upgrade_choice_modal";
import { ProQuantityModal } from "./pro_quantity_modal";
import { SubscriptionUpgradeModal } from "./subscription_upgrade_modal";
import { ServerActivationModal, ActivationType } from "./pro_assignment_modal";
import { isFeatureEnabled } from "../../../../../../src/feature_flags";

// Type for upgradeable license from the API
type UpgradeableLicense =
	RouterInferenceHelpers["premium"]["getUpgradeableLicenses"]["output"]["licenses"][number];

export function PricingTable(props: {
	link_type: "anchor" | "Link";
	current_pricing: RouterInferenceHelpers["premium"]["getAllProductsPrices"]["output"];
	has_used_free_trial?: boolean;
}) {
	const [selected_span, setSpan] = useState<PaymentInterval>(
		PaymentInterval.Month
	);
	const history = useHistory();
	const guild_list = useContext(GuildListContext);

	// Upgrade flow state
	const [showUpgradeChoice, setShowUpgradeChoice] = useState(false);
	const [showQuantityPicker, setShowQuantityPicker] = useState(false);
	const [showSubscriptionConfirm, setShowSubscriptionConfirm] = useState(false);
	const [showActivationModal, setShowActivationModal] = useState(false);
	const [activationType, setActivationType] = useState<ActivationType>("premium");
	const [selectedLicense, setSelectedLicense] = useState<UpgradeableLicense | null>(null);
	const [selectedProCount, setSelectedProCount] = useState(1);
	// Track pending unassigned count from just-completed upgrade (before webhook processes)
	const [pendingUnassignedCount, setPendingUnassignedCount] = useState<number | null>(null);
	// Loading state for one-time upgrade checkout redirect
	const [isUpgradeLoading, setIsUpgradeLoading] = useState(false);

	// Only fetch user-specific data on the dashboard (when link_type is "Link")
	// On the static premium page (link_type "anchor"), user is not authenticated
	const isOnDashboard = props.link_type === "Link";

	// Query for upgradeable licenses
	const upgradeableLicensesQuery = trpc.premium.getUpgradeableLicenses.useQuery(
		undefined,
		{ enabled: isOnDashboard }
	);

	// Query for premium details (needed for Pro assignment modal)
	const premiumDetailsQuery = trpc.premium.getUserPremiumDetails.useQuery(
		undefined,
		{ enabled: isOnDashboard }
	);

	// Get trpc context for direct API calls with fresh parameters
	const trpcContext = trpc.useContext();

	// Mutation for assigning Premium to guilds
	const assignPremiumMutation = trpc.premium.assignPremiumToGuilds.useMutation({
		onSuccess: () => {
			setShowActivationModal(false);
			setPendingUnassignedCount(null);
			trpcContext.premium.invalidate();
		},
	});

	// Mutation for assigning Pro to guilds
	const assignProMutation = trpc.premium.assignSeshProToGuilds.useMutation({
		onSuccess: () => {
			setShowActivationModal(false);
			setPendingUnassignedCount(null);
			trpcContext.premium.invalidate();
		},
	});

	const { current_pricing } = props;

	// Detect success redirect and show activation modal if needed
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get("success") === "true") {
			// Clean up URL
			window.history.replaceState({}, "", window.location.pathname);

			// Refetch premium details to check for unassigned activations
			premiumDetailsQuery.refetch().then((result) => {
				if (result.data) {
					// Check for unassigned Pro first (higher priority)
					const unassignedPro =
						result.data.max_sesh_pro_activations -
						result.data.sesh_pro_activated_servers_count;
					if (unassignedPro > 0) {
						setActivationType("pro");
						setShowActivationModal(true);
						return;
					}

					// Check for unassigned Premium
					const unassignedPremium =
						result.data.max_activated_servers -
						result.data.transferrable_activated_servers_count;
					if (unassignedPremium > 0) {
						setActivationType("premium");
						setShowActivationModal(true);
					}
				}
			});
		}
	}, []);

	// Handle sesh Pro purchase click
	const handleSeshProPurchaseClick = () => {
		const licenses = upgradeableLicensesQuery.data?.licenses ?? [];

		if (licenses.length > 0) {
			// User has upgradeable licenses - show upgrade choice modal
			setShowUpgradeChoice(true);
		} else {
			// No upgradeable licenses - proceed to normal checkout
			const url = common_utils.getStripeRedirectURL({
				type: "checkout",
				product_id: getSeshProStripeProductId(),
				subscription_span: selected_span.toString(),
				should_force_no_coupon: "false",
			});
			if (props.link_type === "Link") {
				history.push(url);
			} else {
				window.location.href = url;
			}
		}
	};

	// Handle upgrade selected from the choice modal
	const handleUpgradeSelected = (license: UpgradeableLicenseInfo) => {
		// Find the corresponding upgradeable license with full info
		const upgradeableLicense = upgradeableLicensesQuery.data?.licenses.find(
			(l) => l.id === license.id
		);

		if (!upgradeableLicense) return;

		setSelectedLicense(upgradeableLicense);

		if (upgradeableLicense.is_bundle && upgradeableLicense.available_pro_slots > 1) {
			// Bundle with multiple slots - show quantity picker
			setShowUpgradeChoice(false);
			setShowQuantityPicker(true);
		} else if (upgradeableLicense.is_subscription) {
			// Single subscription - go straight to confirmation
			setShowUpgradeChoice(false);
			setSelectedProCount(1);
			setShowSubscriptionConfirm(true);
		} else {
			// One-time purchase - redirect to checkout (keep modal open, show loading)
			handleOneTimeUpgrade(upgradeableLicense, 1);
		}
	};

	// Handle quantity confirmed from the quantity picker
	const handleQuantityConfirmed = (count: number) => {
		setSelectedProCount(count);

		if (selectedLicense?.is_subscription) {
			// Subscription - close quantity picker and show confirmation modal
			setShowQuantityPicker(false);
			setShowSubscriptionConfirm(true);
		} else {
			// One-time - keep modal open, show loading, redirect to checkout
			handleOneTimeUpgrade(selectedLicense!, count);
		}
	};

	// Handle one-time upgrade checkout
	const handleOneTimeUpgrade = async (license: UpgradeableLicense, count: number) => {
		setIsUpgradeLoading(true);
		try {
			// Use trpcContext.fetch() with explicit parameters to avoid stale closure issues
			// (React state updates are async, so selectedLicense may not be updated yet)
			const result = await trpcContext.premium.getOneTimeUpgradeCheckout.fetch({
				entitlement_id: license.id,
				pro_count: count,
			});
			if (result?.url) {
				window.location.href = result.url;
			} else {
				setIsUpgradeLoading(false);
			}
		} catch (error) {
			console.error("Failed to get upgrade checkout URL:", error);
			setIsUpgradeLoading(false);
		}
	};

	// Handle buy new license from the choice modal
	const handleBuyNew = () => {
		setShowUpgradeChoice(false);
		const url = common_utils.getStripeRedirectURL({
			type: "checkout",
			product_id: getSeshProStripeProductId(),
			subscription_span: selected_span.toString(),
			should_force_no_coupon: "false",
		});
		if (props.link_type === "Link") {
			history.push(url);
		} else {
			window.location.href = url;
		}
	};

	// Handle subscription upgrade success
	const handleSubscriptionUpgradeSuccess = () => {
		setShowSubscriptionConfirm(false);
		setSelectedLicense(null);

		// Immediately show Pro activation modal with the selected count
		// (don't wait for webhook to process - use the count we just upgraded)
		setPendingUnassignedCount(selectedProCount);
		setActivationType("pro");
		setShowActivationModal(true);

		// Invalidate all premium queries to ensure fresh data
		// This is more reliable than refetch() as it clears the cache
		trpcContext.premium.invalidate();
	};

	// Handle server activation assignment
	const handleActivationAssignment = (guildIds: string[]) => {
		console.log("handleActivationAssignment called with:", guildIds);
		console.log("activationType:", activationType);
		if (activationType === "pro") {
			assignProMutation.mutate({ guild_ids: guildIds });
		} else {
			assignPremiumMutation.mutate({ guild_ids: guildIds });
		}
	};

	// Convert upgradeable licenses to the format expected by UpgradeChoiceModal
	const licensesForUpgradeModal: UpgradeableLicenseInfo[] = (
		upgradeableLicensesQuery.data?.licenses ?? []
	).map((license) => ({
		id: license.id,
		product_id: license.product_id,
		payment_interval: license.payment_interval,
		activations: license.is_bundle ? 3 : 1,
	}));

	const tiers: JSX.Element[] = [];

	const tier_descriptors = getTiersWithoutPrices();

	for (const product_id in tier_descriptors) {
		const casted_product_id = product_id as StripeProductId;
		const current_product = tier_descriptors[casted_product_id];

		// Skip all sesh Pro products - we render sesh Pro separately at the end
		if (isSeshProProduct(product_id) || current_product.title === "sesh Pro") {
			continue;
		}

		for (const current_span in current_product.prices) {
			const span_enum = Number.parseInt(current_span);

			if (PaymentInterval[span_enum] !== PaymentInterval[selected_span]) {
				continue;
			}

			tiers.push(
				<PricingTier
					key={product_id}
					type="regular"
					has_used_free_trial={props.has_used_free_trial}
					product_id={casted_product_id}
					span={span_enum}
					link_type={props.link_type}
					price={current_pricing?.prices[product_id][span_enum]}
					active_coupon={current_pricing?.active_coupon}
					title={current_product.title}
					features={[
						span_enum === PaymentInterval.OneTime ? (
							<>
								<WithTooltip
									style="Info"
									tooltip={
										<div className="max-w-[15rem] whitespace-normal">
											By purchasing a one-time payment
											membership, you will receive premium
											benefits for the lifetime of sesh.
											We intend to continue running sesh
											indefinitely, but there could be a
											(very unlikely) point at which we no
											longer can due to issues with third
											party services (e.g. Discord, Google
											Calendar, Stripe, etc) or other
											unforeseen circumstances.
										</div>
									}
								>
									<span className="border-b border-dotted border-current">
										Lifetime
									</span>
								</WithTooltip>{" "}
								premium for {current_product.activations} server
								{current_product.activations > 1 ? "s" : ""}
							</>
						) : (
							`Premium for ${current_product.activations} server${
								current_product.activations > 1 ? "s" : ""
							}`
						),
						...current_product.features,
					]}
					is_best_deal={current_product.is_best_deal}
					tag_type={
						current_product.is_best_deal ? "Best deal" : undefined
					}
				/>
			);
		}
	}

	// Add sesh Pro tier at the end (rightmost position)
	const sesh_pro_product_id = getSeshProStripeProductId();
	const sesh_pro_price = current_pricing?.prices[sesh_pro_product_id]?.[selected_span];

	// Determine sesh Pro button text based on existing licenses and trial status
	const hasUpgradeableLicenses = (upgradeableLicensesQuery.data?.licenses.length ?? 0) > 0;
	const seshProButtonText = hasUpgradeableLicenses
		? "Upgrade"
		: selected_span === PaymentInterval.OneTime
		? "Get Access Now"
		: props.has_used_free_trial || !isFeatureEnabled("FreeTrial")
		? "Subscribe Now"
		: "Start Free Trial";

	tiers.push(
		<PricingTier
			key="sesh_pro"
			type="regular"
			has_used_free_trial={props.has_used_free_trial}
			product_id={sesh_pro_product_id}
			span={selected_span}
			link_type={props.link_type}
			price={sesh_pro_price}
			active_coupon={current_pricing?.active_coupon}
			title="sesh Pro"
			features={[
				<div className="flex flex-col">
					<span className="font-semibold text-white">
						Premium for 1 server
					</span>
					<span className="text-xs text-slate-400">
						All features included, plus more
					</span>
				</div>,
				<div className="flex flex-col">
					<span className="font-semibold text-white">API Access</span>
					<span className="text-xs text-slate-400">
						Automate events and integrations
					</span>
				</div>,
				<div className="flex flex-col">
					<a
						href="/manual#bot_customization"
						className="inline-flex items-center gap-1 font-semibold text-white underline decoration-white/30 underline-offset-2 transition-colors hover:text-sesh-primary hover:decoration-sesh-primary/50"
					>
						Custom Branding
						<i className="fas fa-external-link-alt text-[10px] opacity-50"></i>
					</a>
					<span className="text-xs text-slate-400">
						Your own bot name and avatar
					</span>
				</div>,
				<div className="flex flex-col">
					<span className="font-semibold text-white">
						Webhooks
					</span>
					<span className="text-xs text-slate-400">
						Real-time notifications to your services
					</span>
				</div>,
				<div className="flex flex-col">
					<span className="font-semibold text-white">
						Priority Support
					</span>
					<span className="text-xs text-slate-400">
						Direct access via Discord and email
					</span>
				</div>,
				<div className="flex flex-col">
					<span className="font-semibold text-white">
						Business License
					</span>
					<span className="text-xs text-slate-400">
						Commercial use fully permitted
					</span>
				</div>,
			]}
			onPurchaseClick={handleSeshProPurchaseClick}
			buttonText={seshProButtonText}
		/>
	);

	// Calculate unassigned counts for activation modal
	const unassignedProCount = premiumDetailsQuery.data
		? premiumDetailsQuery.data.max_sesh_pro_activations -
		  premiumDetailsQuery.data.sesh_pro_activated_servers_count
		: 0;

	const unassignedPremiumCount = premiumDetailsQuery.data
		? premiumDetailsQuery.data.max_activated_servers -
		  premiumDetailsQuery.data.transferrable_activated_servers_count
		: 0;

	// Use pending count if set (from just-completed upgrade), otherwise use query data
	const unassignedCount =
		pendingUnassignedCount !== null
			? pendingUnassignedCount
			: activationType === "pro"
			? unassignedProCount
			: unassignedPremiumCount;

	return (
		<>
			<div className="flex flex-col items-center gap-4">
				{/* <p className="note text-center text-sm font-bold !text-sesh-primary sm:text-base">
					Premium sale is going on now, 20% off until February 9th!
				</p> */}
				<SeshAddonInput
					value={selected_span}
					setValue={setSpan}
					buttons={[
						{
							label: PaymentIntervalToActiveName[
								PaymentInterval.Month
							],
							value: PaymentInterval.Month,
						},
						{
							label: (
								<div className="flex items-center gap-1">
									<span>
										{
											PaymentIntervalToActiveName[
												PaymentInterval.Year
											]
										}
									</span>
									<span
										className={`text-[10px] ${
											selected_span ===
											PaymentInterval.Year
												? "text-slate-600"
												: "text-green-400"
										}`}
									>
										(save 30%)
									</span>
								</div>
							),
							value: PaymentInterval.Year,
						},
						{
							label: PaymentIntervalToActiveName[
								PaymentInterval.OneTime
							],
							value: PaymentInterval.OneTime,
						},
					]}
				/>
				<div className="mt-3 flex w-full flex-wrap justify-center gap-2">
					{tiers}
				</div>
				<div className="text-center">
					<a
						href="#faq"
						className="inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-sesh-primary"
					>
						<i className="fas fa-question-circle"></i>
						<span>
							View FAQ for info on transfers, commercial use & more
						</span>
					</a>
				</div>
				<div className="mt-4 flex w-full justify-center">
					<div className="w-full max-w-[800px] rounded-lg border border-slate-700 bg-slate-800/30 p-4 text-center">
						<div className="flex items-center justify-center gap-2">
							<i className="fas fa-robot text-lg text-sesh-primary/70"></i>
							<div className="text-sm">
								Looking for custom features or a completely
								custom bot?{" "}
								<a
									href="#custom-bot"
									className="text-sesh-primary hover:underline"
								>
									See details
								</a>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Upgrade Choice Modal */}
			<UpgradeChoiceModal
				open={showUpgradeChoice}
				onClose={() => {
					if (!isUpgradeLoading) {
						setShowUpgradeChoice(false);
					}
				}}
				licenses={licensesForUpgradeModal}
				onUpgrade={handleUpgradeSelected}
				onBuyNew={handleBuyNew}
				isLoading={isUpgradeLoading}
			/>

			{/* Pro Quantity Modal */}
			{selectedLicense && (
				<ProQuantityModal
					open={showQuantityPicker}
					onClose={() => {
						if (!isUpgradeLoading) {
							setShowQuantityPicker(false);
							setSelectedLicense(null);
						}
					}}
					availableSlots={selectedLicense.available_pro_slots}
					currentProCount={selectedLicense.current_pro_addon_count}
					onConfirm={handleQuantityConfirmed}
					isLoading={isUpgradeLoading}
				/>
			)}

			{/* Subscription Upgrade Confirmation Modal */}
			{selectedLicense && (
				<SubscriptionUpgradeModal
					open={showSubscriptionConfirm}
					onClose={() => {
						setShowSubscriptionConfirm(false);
						setSelectedLicense(null);
					}}
					entitlementId={selectedLicense.id}
					proCount={selectedProCount}
					isBundle={selectedLicense.is_bundle}
					onSuccess={handleSubscriptionUpgradeSuccess}
				/>
			)}

			{/* Server Activation Modal (Premium or Pro) */}
			<ServerActivationModal
				open={showActivationModal}
				onClose={() => {
					setShowActivationModal(false);
					setPendingUnassignedCount(null);
				}}
				unassignedCount={unassignedCount}
				guilds={guild_list}
				onAssign={handleActivationAssignment}
				type={activationType}
			/>
		</>
	);
}

export function centsToDollars(cents: number) {
	return (cents / 100).toFixed(2);
}
